/** GL-SPEC-APP-002 Phase B — Document Review Module UI.
 *
 * Flow:
 *   1. Inventory screen (initial state) — 11 toggle rows; consultant marks each Present / Not Present.
 *   2. Review list — shows present docs with status (Not Started / In Progress / Complete);
 *      missing docs flagged as "Not at facility — missing".
 *   3. Per-doc review screen — element groups with 3-state radios + notes, plus Section
 *      Findings Summary (priority + notes).
 */
import { useEffect, useMemo, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useOnline } from '@/lib/useOnline';
import { Spinner } from '@/components/Common';
import {
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  CaretLeftIcon,
  CaretRightIcon,
  WarningIcon,
  ListChecksIcon,
  FloppyDiskIcon,
} from '@phosphor-icons/react';

const STATES = [
  { key: 'present_adequate', label: 'Present & Adequate', icon: CheckCircleIcon, color: 'var(--gl-p3)' },
  { key: 'missing_incomplete', label: 'Missing / Incomplete', icon: XCircleIcon, color: 'var(--gl-p1)' },
  { key: 'na', label: 'N/A', icon: MinusCircleIcon, color: 'var(--gl-muted)' },
];

const SECTION_PRIORITIES = [
  { key: 'critical', label: 'Critical', color: 'var(--gl-p1)', desc: 'Immediate OSHA exposure / imminent danger' },
  { key: 'serious', label: 'Serious', color: 'var(--gl-p2)', desc: 'Significant gap that needs prompt action' },
  { key: 'documentation_gap', label: 'Documentation Gap', color: 'var(--gl-navy)', desc: 'Paperwork incomplete but no immediate exposure' },
];


export default function DocReviewTab({ eng }) {
  const [template, setTemplate] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [summary, setSummary] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'inventory' | 'review'
  const [activeReviewId, setActiveReviewId] = useState(null);
  const online = useOnline();

  async function load() {
    try {
      const [tpl, lst] = await Promise.all([
        api.get('/doc_review_template'),
        api.get(`/engagements/${eng.id}/doc_reviews`),
      ]);
      setTemplate(tpl.data.docs || []);
      setReviews(lst.data.reviews || []);
      setSummary(lst.data.summary || null);
      // If no inventory done yet, start in inventory mode
      if (!lst.data.reviews || lst.data.reviews.length === 0) {
        setView('inventory');
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setTemplate([]);
      setReviews([]);
    }
  }
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { load(); }, [eng.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (template === null || reviews === null) return <Spinner label="Loading documents…" />;

  if (view === 'inventory') {
    return (
      <InventoryScreen
        template={template}
        reviews={reviews}
        eng={eng}
        onReviewsChanged={(r) => {
          setReviews(r);
        }}
        onSaved={(r) => {
          setReviews(r.reviews);
          setSummary(r.summary);
          setView('list');
        }}
        onCancel={() => setView('list')}
        onOpenReview={(id) => { setActiveReviewId(id); setView('review'); }}
      />
    );
  }

  if (view === 'review' && activeReviewId) {
    const review = reviews.find((r) => r.id === activeReviewId);
    if (!review) {
      setView('list');
      return null;
    }
    return (
      <ReviewScreen
        review={review}
        online={online}
        onBack={() => { setView('list'); load(); }}
        onChange={(updated) => {
          setReviews((arr) => arr.map((r) => (r.id === updated.id ? updated : r)));
        }}
      />
    );
  }

  return (
    <ListScreen
      reviews={reviews}
      template={template}
      summary={summary}
      onEditInventory={() => setView('inventory')}
      onOpenReview={(id) => { setActiveReviewId(id); setView('review'); }}
    />
  );
}


/* ========== Screen 1: Inventory ========== */
function InventoryScreen({ template, reviews: initialReviews, eng, onSaved, onCancel, onOpenReview, onReviewsChanged }) {
  // We keep local reviews so auto-save updates immediately without round-tripping the parent
  const [reviews, setReviews] = useState(initialReviews);
  const existing = useMemo(() => {
    const m = new Map();
    for (const r of reviews) m.set(r.doc_key, r);
    return m;
  }, [reviews]);

  // null = unset, true = present, false = not present
  const [picks, setPicks] = useState(() => {
    const out = {};
    for (const d of template) {
      const prior = existing.get(d.key);
      out[d.key] = prior ? prior.is_present : null;
    }
    return out;
  });
  const [otherLabel, setOtherLabel] = useState(() => existing.get('other')?.other_label || '');
  const [busyKey, setBusyKey] = useState(null);

  async function pick(key, value) {
    // Optimistic local update first
    setPicks((p) => ({ ...p, [key]: value }));
    setBusyKey(key);
    try {
      const inventory = [{
        doc_key: key,
        is_present: !!value,
        ...(key === 'other' ? { other_label: otherLabel } : {}),
      }];
      const { data } = await api.post(`/engagements/${eng.id}/doc_reviews/inventory`, { inventory });
      const updated = data.reviews || [];
      setReviews(updated);
      // Propagate to parent so "Open Detailed Review" can find the review by id
      if (onReviewsChanged) onReviewsChanged(updated);
    } catch (e) {
      // Roll back on failure
      setPicks((p) => ({ ...p, [key]: existing.get(key)?.is_present ?? null }));
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  }

  async function done() {
    // Re-fetch to ensure parent has latest, then transition to list view
    try {
      const { data } = await api.get(`/engagements/${eng.id}/doc_reviews`);
      onSaved({ reviews: data.reviews || [], summary: data.summary || null });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-4" data-testid="doc-review-inventory">
      <div className="gl-card" style={{ borderLeft: '4px solid var(--gl-navy)' }}>
        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
          <ListChecksIcon size={22} weight="fill" style={{ color: 'var(--gl-navy)' }} />
          <div
            className="font-display font-extrabold uppercase tracking-widest text-lg"
            style={{ color: 'var(--gl-dark)', letterSpacing: '0.08em' }}
          >
            Document Inventory
          </div>
        </div>
        <p className="text-sm" style={{ color: 'var(--gl-muted)' }}>
          Tap <b style={{ color: 'var(--gl-p3)' }}>Present</b> or{' '}
          <b style={{ color: 'var(--gl-p1)' }}>Not Present</b> for each document. Picks save
          automatically. Present docs immediately reveal an{' '}
          <b>Open Detailed Review &rarr;</b> button to grade each CFR element.
        </p>
      </div>

      <div className="space-y-2">
        {template.map((d) => {
          const review = existing.get(d.key);
          const isPresent = picks[d.key] === true;
          const isMissing = picks[d.key] === false;
          const isBusy = busyKey === d.key;
          return (
            <div
              key={d.key}
              className="gl-card flex flex-wrap items-center gap-3"
              data-testid={`inv-row-${d.key}`}
              style={{
                borderLeft: isPresent ? '4px solid var(--gl-p3)' : isMissing ? '4px solid var(--gl-p1)' : '4px solid transparent',
                transition: 'border-color 0.2s',
              }}
            >
              <div className="flex-1 min-w-0">
                <div
                  className="font-display font-extrabold uppercase tracking-wide"
                  style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.05em' }}
                >
                  {d.label}
                </div>
                {d.cfr && (
                  <div className="text-xs uppercase font-bold mt-0.5" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                    {d.cfr}
                  </div>
                )}
                {d.high_priority && d.high_priority_note && (
                  <div className="text-xs mt-1 p-2 border-l-2 inline-flex items-start gap-1"
                       style={{ borderColor: 'var(--gl-p2)', background: '#fdf6e3', color: 'var(--gl-dark)' }}>
                    <WarningIcon size={14} weight="fill" style={{ color: 'var(--gl-p2)', flexShrink: 0, marginTop: 2 }} />
                    <span>{d.high_priority_note}</span>
                  </div>
                )}
                {d.key === 'other' && isPresent && (
                  <input
                    type="text"
                    className="gl-input mt-2"
                    placeholder="Describe the document…"
                    value={otherLabel}
                    onChange={(e) => setOtherLabel(e.target.value)}
                    data-testid="inv-other-label"
                  />
                )}
                {/* "Open Detailed Review →" appears as soon as Present is saved */}
                {isPresent && review && review.id && onOpenReview && (
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border-2 text-xs font-bold uppercase tracking-wider"
                    style={{
                      background: '#eff4fb',
                      borderColor: 'var(--gl-navy)',
                      color: 'var(--gl-navy)',
                      fontFamily: 'Inter',
                      letterSpacing: '0.08em',
                    }}
                    onClick={() => onOpenReview(review.id)}
                    data-testid={`inv-${d.key}-open-review`}
                  >
                    <CaretRightIcon size={14} weight="bold" />
                    Open Detailed Review
                  </button>
                )}
              </div>
              <div className="flex gap-1.5 flex-wrap items-center" role="radiogroup" aria-label={d.label}>
                {isBusy && (
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                    Saving…
                  </span>
                )}
                <PickBtn active={isPresent} color="var(--gl-p3)" label="Present" testId={`inv-${d.key}-present`}
                         onClick={() => pick(d.key, true)} disabled={isBusy} />
                <PickBtn active={isMissing} color="var(--gl-p1)" label="Not Present" testId={`inv-${d.key}-missing`}
                         onClick={() => pick(d.key, false)} disabled={isBusy} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 sticky bottom-0 py-3" style={{ background: 'rgba(255,255,255,0.95)' }}>
        <button type="button" className="gl-btn gl-btn-primary" onClick={done} data-testid="inv-save-btn">
          <CaretRightIcon size={16} weight="bold" /> Done — View Review List
        </button>
        <button type="button" className="gl-btn gl-btn-secondary" onClick={onCancel} data-testid="inv-cancel-btn">
          Cancel
        </button>
      </div>
    </div>
  );
}

function PickBtn({ active, color, label, onClick, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-3 h-10 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors"
      style={{
        borderColor: active ? color : '#d1d5db',
        background: active ? color : '#fff',
        color: active ? '#fff' : 'var(--gl-dark)',
        fontFamily: 'Inter',
        letterSpacing: '0.08em',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'wait' : 'pointer',
      }}
      data-testid={testId}
    >
      {label}
    </button>
  );
}


/* ========== Screen 2: List ========== */
function ListScreen({ reviews, template, summary, onEditInventory, onOpenReview }) {
  // Order reviews by the template order, including not-yet-inventoried docs
  const tplOrder = new Map(template.map((d, i) => [d.key, i]));
  const sorted = [...reviews].sort(
    (a, b) => (tplOrder.get(a.doc_key) ?? 999) - (tplOrder.get(b.doc_key) ?? 999)
  );
  const present = sorted.filter((r) => r.is_present === true);
  const missing = sorted.filter((r) => r.is_present === false);

  return (
    <div className="space-y-4" data-testid="doc-review-list">
      {summary && (
        <div className="gl-card grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Present" value={summary.docs_present} accent="var(--gl-p3)" />
          <Stat label="Not Present" value={summary.docs_not_present} accent="var(--gl-p1)" />
          <Stat
            label="Element Compliance"
            value={summary.score_pct == null ? '—' : `${Math.round(summary.score_pct)}%`}
            accent="var(--gl-navy)"
          />
          <Stat label="Critical Missing" value={summary.critical_missing} accent="var(--gl-p1)" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          className="gl-btn gl-btn-secondary !h-10 !px-4 !text-xs"
          onClick={onEditInventory}
          data-testid="edit-inventory-btn"
        >
          <ListChecksIcon size={16} weight="bold" /> Edit Inventory
        </button>
        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
          {reviews.length} of {template.length} documents inventoried
        </div>
      </div>

      {present.length > 0 && (
        <section>
          <h3
            className="font-display font-extrabold uppercase text-lg mb-2"
            style={{ color: 'var(--gl-dark)', letterSpacing: '0.05em' }}
          >
            Present at Facility ({present.length})
          </h3>
          <div className="space-y-2">
            {present.map((r) => {
              const completed = (r.items || []).filter((it) => it.state).length;
              const total = (r.items || []).length;
              const missingCount = (r.items || []).filter((it) => it.state === 'missing_incomplete').length;
              const criticalMissing = (r.items || []).filter((it) => it.state === 'missing_incomplete' && it.critical).length;
              const status = total === 0
                ? 'Inventory only'
                : completed === 0 ? 'Not started'
                : completed < total ? `In progress (${completed}/${total})`
                : 'Review complete';
              return (
                <button
                  key={r.id}
                  type="button"
                  className="gl-card w-full text-left hover:border-gray-400 transition-colors"
                  onClick={() => onOpenReview(r.id)}
                  data-testid={`open-doc-${r.doc_key}`}
                  style={{ borderLeft: criticalMissing > 0 ? '4px solid var(--gl-p1)' : missingCount > 0 ? '4px solid var(--gl-p2)' : '4px solid var(--gl-p3)' }}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div
                        className="font-display font-extrabold uppercase tracking-wide"
                        style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.05em' }}
                      >
                        {r.doc_key === 'other' && r.other_label ? `Other: ${r.other_label}` : r.doc_label}
                      </div>
                      <div className="text-xs uppercase font-bold mt-0.5" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                        {r.cfr || ''}
                      </div>
                      <div className="mt-1 flex gap-2 items-center flex-wrap text-xs">
                        <span className="font-bold uppercase tracking-wider" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                          {status}
                        </span>
                        {criticalMissing > 0 && (
                          <span className="px-1.5 py-0.5 font-bold uppercase" style={{ background: 'var(--gl-p1)', color: '#fff', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                            {criticalMissing} critical missing
                          </span>
                        )}
                        {r.section_priority && (
                          <span className="px-1.5 py-0.5 font-bold uppercase" style={{ background: SECTION_PRIORITIES.find((s) => s.key === r.section_priority)?.color || '#666', color: '#fff', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                            {SECTION_PRIORITIES.find((s) => s.key === r.section_priority)?.label}
                          </span>
                        )}
                      </div>
                    </div>
                    <CaretRightIcon size={20} weight="bold" style={{ color: 'var(--gl-navy)' }} />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {missing.length > 0 && (
        <section>
          <h3
            className="font-display font-extrabold uppercase text-lg mb-2"
            style={{ color: 'var(--gl-p1)', letterSpacing: '0.05em' }}
          >
            Not Present at Facility ({missing.length})
          </h3>
          <div className="space-y-2">
            {missing.map((r) => (
              <div
                key={r.id}
                className="gl-card"
                style={{ borderLeft: '4px solid var(--gl-p1)' }}
                data-testid={`missing-doc-${r.doc_key}`}
              >
                <div
                  className="font-display font-extrabold uppercase tracking-wide"
                  style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.05em' }}
                >
                  {r.doc_key === 'other' && r.other_label ? `Other: ${r.other_label}` : r.doc_label}
                </div>
                <div className="text-xs uppercase font-bold mt-0.5" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                  {r.cfr || ''} · Will be flagged as MISSING in the report
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-bold" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
        {label}
      </div>
      <div className="font-display font-extrabold" style={{ color: accent, fontSize: '1.75rem', fontFamily: 'Inter' }}>
        {value}
      </div>
    </div>
  );
}


/* ========== Screen 3: Per-Doc Review ========== */
function ReviewScreen({ review, online, onBack, onChange }) {
  const [local, setLocal] = useState(review);
  const [savingItems, setSavingItems] = useState(new Set());
  const [savingSection, setSavingSection] = useState(false);

  useEffect(() => { setLocal(review); }, [review.id, review.updated_at]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const it of local.items || []) {
      if (!m.has(it.group_label)) m.set(it.group_label, []);
      m.get(it.group_label).push(it);
    }
    return Array.from(m.entries());
  }, [local.items]);

  async function setItemState(item, patch) {
    const newItems = (local.items || []).map((it) => (it.key === item.key ? { ...it, ...patch } : it));
    setLocal({ ...local, items: newItems });
    setSavingItems((s) => new Set(s).add(item.key));
    try {
      const { data } = await api.patch(
        `/doc_reviews/${review.id}/items/${item.key}`,
        patch,
      );
      setLocal(data);
      onChange(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingItems((s) => {
        const next = new Set(s);
        next.delete(item.key);
        return next;
      });
    }
  }

  async function setSection(patch) {
    setLocal((l) => ({ ...l, ...patch }));
    setSavingSection(true);
    try {
      const { data } = await api.patch(`/doc_reviews/${review.id}`, patch);
      setLocal(data);
      onChange(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSection(false);
    }
  }

  const completedCount = (local.items || []).filter((it) => it.state).length;
  const totalCount = (local.items || []).length;

  return (
    <div className="space-y-4" data-testid="doc-review-screen">
      <button
        type="button"
        className="gl-btn gl-btn-secondary !h-9 !px-3 !text-xs"
        onClick={onBack}
        data-testid="doc-review-back-btn"
      >
        <CaretLeftIcon size={14} weight="bold" /> Back to documents
      </button>

      <div className="gl-card" style={{ borderLeft: '4px solid var(--gl-navy)' }}>
        <div
          className="font-display font-extrabold uppercase text-2xl tracking-wide"
          style={{ color: 'var(--gl-dark)', letterSpacing: '0.04em' }}
        >
          {local.doc_key === 'other' && local.other_label ? `Other: ${local.other_label}` : local.doc_label}
        </div>
        {local.cfr && (
          <div
            className="text-xs uppercase font-bold mt-1"
            style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
          >
            {local.cfr}
          </div>
        )}
        {local.high_priority && local.high_priority_note && (
          <div
            className="text-sm mt-3 p-3 border-l-4 flex items-start gap-2"
            style={{ borderColor: 'var(--gl-p2)', background: '#fdf6e3' }}
          >
            <WarningIcon size={20} weight="fill" style={{ color: 'var(--gl-p2)', flexShrink: 0 }} />
            <span style={{ color: 'var(--gl-dark)' }}>{local.high_priority_note}</span>
          </div>
        )}
        <div className="mt-3 text-xs uppercase font-bold tracking-widest"
             style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
          {completedCount} of {totalCount} elements reviewed
        </div>
      </div>

      {groups.map(([groupLabel, items]) => (
        <section key={groupLabel} className="space-y-2">
          <h4
            className="font-display font-extrabold uppercase text-base"
            style={{ color: 'var(--gl-navy)', letterSpacing: '0.06em' }}
          >
            {groupLabel}
          </h4>
          <div className="space-y-2">
            {items.map((item) => (
              <ElementRow
                key={item.key}
                item={item}
                saving={savingItems.has(item.key)}
                onState={(state) => setItemState(item, { state })}
                onNotes={(notes) => setItemState(item, { notes })}
              />
            ))}
          </div>
        </section>
      ))}

      <section className="gl-card" style={{ borderLeft: '4px solid var(--gl-gold)' }}>
        <div className="flex items-baseline gap-2 flex-wrap mb-2">
          <h4
            className="font-display font-extrabold uppercase text-lg"
            style={{ color: 'var(--gl-dark)', letterSpacing: '0.06em' }}
          >
            Section Findings Summary
          </h4>
          {savingSection && <span className="text-xs" style={{ color: 'var(--gl-muted)' }}>Saving…</span>}
        </div>
        <div className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
          Overall priority for this document
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {SECTION_PRIORITIES.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setSection({ section_priority: p.key })}
              className="px-3 h-10 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors"
              style={{
                borderColor: local.section_priority === p.key ? p.color : '#d1d5db',
                background: local.section_priority === p.key ? p.color : '#fff',
                color: local.section_priority === p.key ? '#fff' : 'var(--gl-dark)',
                fontFamily: 'Inter',
                letterSpacing: '0.08em',
              }}
              title={p.desc}
              data-testid={`section-priority-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label
          className="text-xs uppercase tracking-widest font-bold"
          style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
        >
          Section notes
        </label>
        <textarea
          className="gl-input mt-1"
          rows={3}
          value={local.section_notes || ''}
          onChange={(e) => setLocal({ ...local, section_notes: e.target.value })}
          onBlur={() => setSection({ section_notes: local.section_notes || '' })}
          placeholder="What is/isn't in this document, top recommendation, target date…"
          data-testid="section-notes-input"
        />
        <div className="mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!local.is_complete}
              onChange={(e) => setSection({ is_complete: e.target.checked })}
              data-testid="section-complete-toggle"
            />
            <span className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.06em' }}>
              Mark this document review complete
            </span>
          </label>
        </div>
      </section>
    </div>
  );
}

function ElementRow({ item, saving, onState, onNotes }) {
  const [draftNotes, setDraftNotes] = useState(item.notes || '');
  useEffect(() => { setDraftNotes(item.notes || ''); }, [item.notes]);
  const isCritical = item.critical;
  const isMissing = item.state === 'missing_incomplete';

  return (
    <div
      className="gl-card"
      style={{
        borderLeft: isCritical
          ? `4px solid ${isMissing ? 'var(--gl-p1)' : 'var(--gl-p2)'}`
          : '4px solid #d1d5db',
        background: isCritical && isMissing ? '#fef2f2' : '#fff',
      }}
      data-testid={`doc-item-${item.key}`}
    >
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm" style={{ color: 'var(--gl-dark)' }}>{item.label}</span>
            {isCritical && (
              <span className="text-xs font-bold uppercase px-1.5 py-0.5"
                    style={{ background: 'var(--gl-p1)', color: '#fff', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
                Critical
              </span>
            )}
            {saving && <span className="text-xs" style={{ color: 'var(--gl-muted)' }}>Saving…</span>}
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {STATES.map((s) => {
            const I = s.icon;
            const active = item.state === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => onState(s.key)}
                className="px-2 h-9 border-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-1"
                style={{
                  borderColor: active ? s.color : '#d1d5db',
                  background: active ? s.color : '#fff',
                  color: active ? '#fff' : 'var(--gl-dark)',
                  fontFamily: 'Inter',
                  letterSpacing: '0.06em',
                }}
                title={s.label}
                data-testid={`item-${item.key}-${s.key}`}
              >
                <I size={14} weight="fill" /> {s.label}
              </button>
            );
          })}
        </div>
      </div>
      {(item.state === 'missing_incomplete' || (draftNotes && draftNotes.length > 0)) && (
        <textarea
          className="gl-input mt-2"
          rows={2}
          placeholder={item.state === 'missing_incomplete' ? 'What is missing / what needs fixing?' : 'Optional notes…'}
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
          onBlur={() => { if (draftNotes !== item.notes) onNotes(draftNotes); }}
          data-testid={`item-${item.key}-notes`}
        />
      )}
    </div>
  );
}
